import { useState, useEffect } from "react";
import { Scissors, Clock, Zap, ChevronUp, ChevronDown, History } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface ClipRequestFormProps {
 videoId: string;
 videoDuration: number;
 getCurrentTime: () => number;
}

const formatTime = (seconds: number) => {
 if (isNaN(seconds)) return "00:00:00";
 const h = Math.floor(seconds / 3600);
 const m = Math.floor((seconds % 3600) / 60);
 const s = Math.floor(seconds % 60);
 return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
};

const TimeDial = ({ 
 value, 
 onChange, 
 max, 
 label 
}: { 
 value: number; 
 onChange: (val: number) => void; 
 max: number; 
 label: string 
}) => {
 const hrs = Math.floor(value / 3600);
 const mins = Math.floor((value % 3600) / 60);
 const secs = Math.floor(value % 60);

 const adjust = (type: "h" | "m" | "s", delta: number) => {
 let newVal = value;
 if (type === "h") newVal = (hrs + delta) * 3600 + mins * 60 + secs;
 else if (type === "m") newVal = hrs * 3600 + (mins + delta) * 60 + secs;
 else newVal = hrs * 3600 + mins * 60 + (secs + delta);
 
 if (newVal < 0) newVal = 0;
 if (newVal > max) newVal = max;
 onChange(newVal);
 };

 return (
 <div className="flex flex-col items-center gap-1">
 <span className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60 mb-2">{label}</span>
 <div className="flex items-center gap-2 bg-secondary/30 backdrop-blur-xl p-3 rounded-xl border border-border/80 shadow-2xl group/dial ring-1 ring-primary/5">
 <div className="flex flex-col items-center">
 <button onClick={() => adjust("h", 1)} className="p-1.5 text-muted-foreground/40 hover:text-primary transition-all hover:scale-125"><ChevronUp className="w-3.5 h-3.5" /></button>
 <div className="w-10 h-10 flex items-center justify-center bg-card rounded-2xl border border-border shadow-inner relative overflow-hidden">
 <AnimatePresence mode="wait">
 <motion.span key={hrs} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -20, opacity: 0 }} className="text-base font-mono font-black text-foreground">
 {hrs.toString().padStart(2, "0")}
 </motion.span>
 </AnimatePresence>
 </div>
 <button onClick={() => adjust("h", -1)} className="p-1.5 text-muted-foreground/40 hover:text-primary transition-all hover:scale-125"><ChevronDown className="w-3.5 h-3.5" /></button>
 </div>

 <span className="text-primary font-black text-xl mb-1 opacity-20">:</span>

 <div className="flex flex-col items-center">
 <button onClick={() => adjust("m", 1)} className="p-1.5 text-muted-foreground/40 hover:text-primary transition-all hover:scale-125"><ChevronUp className="w-4 h-4" /></button>
 <div className="w-11 h-11 flex items-center justify-center bg-card rounded-2xl border border-border shadow-inner relative overflow-hidden">
 <AnimatePresence mode="wait">
 <motion.span key={mins} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -20, opacity: 0 }} className="text-lg font-mono font-black text-foreground">
 {mins.toString().padStart(2, "0")}
 </motion.span>
 </AnimatePresence>
 </div>
 <button onClick={() => adjust("m", -1)} className="p-1.5 text-muted-foreground/40 hover:text-primary transition-all hover:scale-125"><ChevronDown className="w-4 h-4" /></button>
 </div>

 <span className="text-primary font-black text-2xl mb-1 opacity-20">:</span>

 <div className="flex flex-col items-center">
 <button onClick={() => adjust("s", 1)} className="p-1.5 text-muted-foreground/40 hover:text-primary transition-all hover:scale-125"><ChevronUp className="w-4 h-4" /></button>
 <div className="w-11 h-11 flex items-center justify-center bg-card rounded-2xl border border-border shadow-inner relative overflow-hidden">
 <AnimatePresence mode="wait">
 <motion.span key={secs} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -20, opacity: 0 }} className="text-lg font-mono font-black text-foreground">
 {secs.toString().padStart(2, "0")}
 </motion.span>
 </AnimatePresence>
 </div>
 <button onClick={() => adjust("s", -1)} className="p-1.5 text-muted-foreground/40 hover:text-primary transition-all hover:scale-125"><ChevronDown className="w-4 h-4" /></button>
 </div>
 
 <div className="flex flex-col gap-1 ml-1 pl-3 border-l border-border/50">
 <button onClick={() => adjust("s", 5)} className="text-[9px] font-black text-muted-foreground hover:text-primary bg-secondary/50 hover:bg-primary/10 px-2 py-1.5 rounded-lg transition-all">+5S</button>
 <button onClick={() => adjust("s", -5)} className="text-[9px] font-black text-muted-foreground hover:text-primary bg-secondary/50 hover:bg-primary/10 px-2 py-1.5 rounded-lg transition-all">-5S</button>
 </div>
 </div>
 </div>
 );
};

const ClipRequestForm = ({ videoId, videoDuration, getCurrentTime }: ClipRequestFormProps) => {
 const { user, profile } = useAuth();
 const [startTime, setStartTime] = useState(0);
 const [endTime, setEndTime] = useState(15);
 const [saving, setSaving] = useState(false);

 useEffect(() => {
 if (videoDuration > 0 && endTime > videoDuration) {
 setEndTime(Math.min(15, videoDuration));
 }
 }, [videoDuration, endTime]);

 const handleSetStart = () => {
 const time = getCurrentTime();
 setStartTime(time);
 if (endTime <= time) setEndTime(Math.min(videoDuration, time + 10));
 };

 const handleSetEnd = () => {
 const time = getCurrentTime();
 setEndTime(time);
 if (startTime >= time) setStartTime(Math.max(0, time - 10));
 if (time > videoDuration && videoDuration > 0) setEndTime(videoDuration);
 };
 
 const handleCaptureLast15s = () => {
 const time = getCurrentTime();
 setEndTime(time);
 setStartTime(Math.max(0, time - 15));
 };

 const handleRequestCut = async () => {
 if (!user) {
 toast.error("Você precisa estar logado para solicitar um corte.");
 return;
 }
 setSaving(true);

 const { error } = await supabase.from("clips_requests").insert({
 user_id: user.id,
 video_id: videoId,
 start_time: startTime,
 end_time: endTime,
 status: "PENDING",
 } as any);

 if (error) {
 toast.error(`Erro ao solicitar corte: ${error.message}`);
 } else {
 toast.success("Solicitação enviada com sucesso! Aguarde o processamento.");
 
 try {
 const { data: adminRoles } = await supabase.from("user_roles").select("user_id").eq("role", "admin");
 const adminIds = new Set(adminRoles?.map(r => r.user_id) || []);
 if (adminIds.size > 0) {
 const userName = profile?.full_name || "um jogador";
 const notifications = Array.from(adminIds).map(id => ({
 user_id: id,
 message: `Nova solicitação de corte de ${userName}!`,
 link: "/admin?tab=clips",
 read: false
 }));
 await supabase.from("notifications").insert(notifications);
 }
 } catch (err) {
 console.error("Erro ao notificar admins:", err);
 }
 }
 setSaving(false);
 };

 return (
 <div className="w-full mt-4 bg-card/60 backdrop-blur-2xl border border-border p-6 md:p-8 rounded-2xl shadow-2xl relative overflow-hidden group hover:border-primary/30 transition-all duration-500">
 <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity" />
 
 <div className="relative z-10 space-y-8">
 <div className="flex items-center justify-between">
 <div className="flex flex-col">
 <h3 className="font-display font-black text-foreground uppercase tracking-tighter  flex items-center gap-2 text-xl md:text-2xl">
 <Scissors className="w-6 h-6 text-primary" />
 Solicitar <span className="text-primary">Corte</span>
 </h3>
 <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.2em] mt-1 ml-8">Recorte as melhores jogadas</p>
 </div>
 <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 shadow-sm">
 <Zap className="w-3 h-3 text-primary animate-pulse" />
 <span className="text-[9px] font-black text-primary uppercase tracking-[0.2em]">SINCRO AI</span>
 </div>
 </div>

 <div className="space-y-6">
 <div className="flex items-center justify-between text-sm">
 <span className="text-muted-foreground font-black uppercase tracking-widest text-[10px] flex items-center gap-2">
 <Clock className="w-4 h-4 text-primary opacity-40" /> Trecho Selecionado
 </span>
 <span className="text-primary font-mono font-black bg-primary/5 px-5 py-2 rounded-2xl border border-primary/20 shadow-inner">
 {formatTime(startTime)} <span className="text-muted-foreground/30 mx-2">/</span> {formatTime(endTime)}
 </span>
 </div>
 
 <div className="flex flex-col lg:flex-row items-center justify-around py-8 bg-secondary/20 rounded-2xl border border-border shadow-inner relative gap-8">
 <TimeDial label="Início do Lance" value={startTime} onChange={setStartTime} max={endTime - 1} />
 <div className="hidden lg:block w-[1px] h-12 bg-border/50" />
 <TimeDial label="Fim do Lance" value={endTime} onChange={setEndTime} max={videoDuration || 180 * 60} />
 </div>

 <div className="space-y-3">
 <Button 
 variant="outline" 
 onClick={handleCaptureLast15s}
 className="w-full bg-secondary/40 border-border hover:bg-primary/10 hover:border-primary/30 text-primary hover:text-primary text-[10px] font-black uppercase tracking-[0.2em] h-14 transition-all rounded-2xl shadow-sm group/cap"
 >
 <History className="w-4 h-4 mr-2 group-hover:rotate-[-45deg] transition-transform" />
 Capturar Últimos 15 Segundos
 </Button>
 
 <div className="flex gap-4">
 <Button 
 variant="outline" 
 onClick={handleSetStart} 
 className="flex-1 bg-card/50 border-border hover:bg-secondary hover:border-primary/30 text-[9px] font-black uppercase tracking-[0.2em] h-12 transition-all rounded-xl"
 >
 Marcar Início
 </Button>
 <Button 
 variant="outline" 
 onClick={handleSetEnd} 
 className="flex-1 bg-card/50 border-border hover:bg-secondary hover:border-primary/30 text-[9px] font-black uppercase tracking-[0.2em] h-12 transition-all rounded-xl"
 >
 Marcar Fim
 </Button>
 </div>
 </div>
 </div>

 <Button
 onClick={handleRequestCut}
 disabled={saving}
 className="w-full bg-primary text-primary-foreground font-black uppercase tracking-[0.2em] shadow-2xl shadow-primary/30 hover:shadow-primary/50 transition-all h-16 text-lg rounded-xl group/btn overflow-hidden relative"
 >
 <div className="absolute inset-0 bg-foreground/10 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 slant" />
 {saving ? (
 "Processando..."
 ) : (
 <span className="flex items-center gap-3 group-hover:scale-105 transition-transform">
 <Scissors className="w-6 h-6" /> Enviar Pedido de Corte
 </span>
 )}
 </Button>
 </div>
 </div>
 );
};

export default ClipRequestForm;
